import React from 'react';

/**
 * BridgeAnimation — abstract animated SVG showing capital + technology
 * flowing between the United States and Africa across an arc.
 * Two side nodes ("Americas" + "Africa") connected by a luminous bezier
 * along which small dots travel in both directions.
 */
const BridgeAnimation = ({ className = '', light = false }) => {
  const usLabel = light ? '#a8842f' : '#e0c674';
  const afLabel = light ? '#268578' : '#7fd6c9';
  return (
    <div className={`relative ${className}`} data-testid="bridge-animation">
      <svg viewBox="0 0 800 280" className="w-full h-auto">
        <defs>
          <linearGradient id="bridge-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="#e0c674" stopOpacity="0.85" />
            <stop offset="50%" stopColor="#7fd6c9" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#7fd6c9" stopOpacity="0.85" />
          </linearGradient>
          <radialGradient id="node-us" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#e0c674" stopOpacity="0.65" />
            <stop offset="60%" stopColor="#c19a3c" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0a1729" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="node-af" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#7fd6c9" stopOpacity="0.65" />
            <stop offset="60%" stopColor="#2f9e8e" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0a1729" stopOpacity="0" />
          </radialGradient>
          <filter id="bg-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* Node glows */}
        <circle cx="140" cy="150" r="80" fill="url(#node-us)" filter="url(#bg-glow)" />
        <circle cx="660" cy="150" r="80" fill="url(#node-af)" filter="url(#bg-glow)" />

        {/* Latitude rings (decorative) */}
        <g fill="none" stroke="rgba(127,214,201,0.12)" strokeWidth="1">
          <ellipse cx="140" cy="150" rx="64" ry="20" />
          <ellipse cx="140" cy="150" rx="48" ry="14" />
          <ellipse cx="660" cy="150" rx="64" ry="20" />
          <ellipse cx="660" cy="150" rx="48" ry="14" />
        </g>

        {/* Connecting bezier path */}
        <path id="bridge-path" d="M180 150 C 320 30, 480 30, 620 150" stroke="url(#bridge-grad)" strokeWidth="2.5" fill="none" />
        <path d="M180 150 C 320 30, 480 30, 620 150" stroke="url(#bridge-grad)" strokeWidth="0.6" fill="none" opacity="0.25" strokeDasharray="6 6" />

        {/* Reverse bezier (slight below) */}
        <path id="bridge-path-return" d="M180 152 C 320 270, 480 270, 620 152" stroke="url(#bridge-grad)" strokeWidth="2.5" fill="none" opacity="0.55" />

        {/* Flow dots — US -> Africa */}
        {[0, 0.8, 1.6, 2.4].map((delay) => (
          <circle key={`fwd-${delay}`} r="4" fill="#e0c674">
            <animateMotion dur="3.2s" repeatCount="indefinite" begin={`${delay}s`}>
              <mpath href="#bridge-path" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" dur="3.2s" repeatCount="indefinite" begin={`${delay}s`} />
          </circle>
        ))}

        {/* Flow dots — Africa -> US (returns: exports, dividends) */}
        {[0.4, 1.2, 2.0, 2.8].map((delay) => (
          <circle key={`rev-${delay}`} r="3.5" fill="#7fd6c9">
            <animateMotion dur="3.2s" repeatCount="indefinite" begin={`${delay}s`} keyPoints="1;0" keyTimes="0;1">
              <mpath href="#bridge-path-return" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" dur="3.2s" repeatCount="indefinite" begin={`${delay}s`} />
          </circle>
        ))}

        {/* Node centers */}
        <circle cx="140" cy="150" r="9" fill="#c19a3c" />
        <circle cx="140" cy="150" r="5" fill="#fff7e6" />
        <circle cx="660" cy="150" r="9" fill="#2f9e8e" />
        <circle cx="660" cy="150" r="5" fill="#e6fff9" />

        {/* Labels */}
        <text x="140" y="225" textAnchor="middle" fill={usLabel} fontSize="12" letterSpacing="2" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700">
          UNITED STATES
        </text>
        <text x="660" y="225" textAnchor="middle" fill={afLabel} fontSize="12" letterSpacing="2" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700">
          AFRICA
        </text>

        {/* Tags on the path */}
        <text x="400" y="55" textAnchor="middle" fill={usLabel} fontSize="10" letterSpacing="1.5" fontFamily="Helvetica, Arial, sans-serif">
          Capital · Technology · Corporations
        </text>
        <text x="400" y="252" textAnchor="middle" fill={afLabel} fontSize="10" letterSpacing="1.5" fontFamily="Helvetica, Arial, sans-serif">
          Resources · Production · Strategic Access
        </text>
      </svg>
    </div>
  );
};

export default BridgeAnimation;
